import { formatDistanceToNow } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Trophy, Mountain, AtSign, UserPlus, Shield, Heart, Video, MapPinOff, CalendarPlus } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { useTranslation } from 'react-i18next';
import type { NotificationItem } from '../../services/notifications';

interface FeedItemCardProps {
  item: NotificationItem;
  onClick: () => void;
  unread?: boolean;
  /** Optional action controls rendered below the notification (e.g. a delete button). */
  actions?: React.ReactNode;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  new_climb:    <Mountain className="h-3.5 w-3.5 text-blue-500" />,
  user_climb:   <Mountain className="h-3.5 w-3.5 text-purple-500" />,
  first_top:    <Trophy className="h-3.5 w-3.5 text-yellow-500" />,
  mention:      <AtSign className="h-3.5 w-3.5 text-violet-500" />,
  new_follower:  <UserPlus className="h-3.5 w-3.5 text-green-500" />,
  badge_earned:  <Shield className="h-3.5 w-3.5 text-amber-500" />,
  video_liked:   <Heart className="h-3.5 w-3.5 text-rose-500" />,
  new_video:     <Video className="h-3.5 w-3.5 text-sky-500" />,
  climb_reported_removed: <MapPinOff className="h-3.5 w-3.5 text-amber-500" />,
  schedule_registration_created: <CalendarPlus className="h-3.5 w-3.5 text-teal-500" />,
};

const TYPE_COLORS: Record<string, string> = {
  new_climb:    'text-blue-600',
  user_climb:   'text-purple-600',
  first_top:    'text-yellow-600',
  mention:      'text-violet-600',
  new_follower:  'text-green-600',
  badge_earned:  'text-amber-600',
  video_liked:   'text-rose-600',
  new_video:     'text-sky-600',
  climb_reported_removed: 'text-amber-600',
  schedule_registration_created: 'text-teal-600',
};

export default function FeedItemCard({ item, onClick, unread, actions }: FeedItemCardProps) {
  const { i18n } = useTranslation();
  const { type } = item;
  const { title, message, image_url, timestamp } = item.attributes;

  const timeAgo = formatDistanceToNow(new Date(timestamp), {
    addSuffix: true,
    locale: i18n.language === 'it' ? it : enUS,
  });

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${unread ? 'border-primary/40 bg-primary/5' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-3">
        <div className="flex gap-3">
          {image_url ? (
            <img
              src={image_url}
              alt={title}
              className="w-16 h-16 object-cover rounded-md flex-shrink-0"
            />
          ) : (
            <div className="w-16 h-16 bg-muted rounded-md flex-shrink-0" />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-0.5">
              <p className="text-sm font-semibold leading-tight line-clamp-2">{title}</p>
              <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 mt-0.5">
                {timeAgo}
              </span>
            </div>

            {message && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-1.5">{message}</p>
            )}

            <div className="flex items-center gap-1.5">
              {TYPE_ICONS[type]}
              <span className={`text-xs font-medium ${TYPE_COLORS[type] ?? ''}`}>
                {type.replace(/_/g, ' ')}
              </span>
            </div>

            {actions && <div className="mt-2 flex items-center gap-2">{actions}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
